import { useI18n } from "../i18n/useI18n";
import { numberLocaleFor } from "../i18n/locale";
import type { UiKey } from "../i18n/ui";
import type { DisplayCurrency } from "../lib/displayCurrency";
import { usdToEurIndicative } from "../lib/displayCurrency";
import { mapExplorerServicesToLineUsd } from "../lib/explorerToModelLines";
import type { CostEstimate } from "../lib/types";

type Props = {
  t: (key: UiKey) => string;
  estimate: CostEstimate;
  explorerByService: Record<string, number> | null;
  displayCurrency: DisplayCurrency;
  onClearExplorer?: () => void;
};

type Row = {
  id: string;
  label: string;
  modelUsd: number;
  csvUsd: number;
};

export function ExplorerVsModelTable({
  t,
  estimate,
  explorerByService,
  displayCurrency,
  onClearExplorer,
}: Props) {
  const { locale } = useI18n();
  const nf = numberLocaleFor(locale);
  const cur = displayCurrency === "EUR" ? "EUR" : "USD";
  const fromUsd = (usd: number) =>
    displayCurrency === "EUR" ? usdToEurIndicative(usd) : usd;

  if (!explorerByService || Object.keys(explorerByService).length === 0) {
    return null;
  }

  const { lineUsd, unmappedUsd } = mapExplorerServicesToLineUsd(explorerByService);

  const fmt = (n: number) =>
    n.toLocaleString(nf, {
      style: "currency",
      currency: cur,
      maximumFractionDigits: 0,
    });

  const idSet = new Set<string>();
  for (const row of estimate.lineItems) idSet.add(row.id);
  for (const id of Object.keys(lineUsd)) idSet.add(id);

  const rows: Row[] = [];
  for (const id of idSet) {
    const modelUsd = estimate.lineItems.find((r) => r.id === id)?.monthlyUsd ?? 0;
    const csvUsd = lineUsd[id] ?? 0;
    if (modelUsd <= 0 && csvUsd <= 0) continue;
    const label = estimate.lineItems.find((r) => r.id === id)?.label ?? id;
    rows.push({ id, label, modelUsd, csvUsd });
  }
  if (unmappedUsd > 0) {
    rows.push({
      id: "__unmapped",
      label: t("csv.compare.unmapped"),
      modelUsd: 0,
      csvUsd: unmappedUsd,
    });
  }

  rows.sort(
    (a, b) =>
      Math.max(b.modelUsd, b.csvUsd) - Math.max(a.modelUsd, a.csvUsd),
  );

  function fmtDelta(modelUsd: number, csvUsd: number): string {
    if (modelUsd <= 0) return "—";
    const pct = Math.round(((csvUsd - modelUsd) / modelUsd) * 1000) / 10;
    const sign = pct > 0 ? "+" : "";
    return `${sign}${pct}%`;
  }

  return (
    <div className="ex-vs">
      <h3>{t("csv.compare.title")}</h3>
      <p className="hint">{t("csv.compare.hint")}</p>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th scope="col">{t("csv.compare.colLine")}</th>
              <th scope="col" className="num">
                {t("csv.compare.colModel")}
              </th>
              <th scope="col" className="num">
                {t("csv.compare.colCsv")}
              </th>
              <th scope="col" className="num">
                {t("csv.compare.colDelta")}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>{r.label}</td>
                <td className="num">
                  {r.modelUsd > 0 ? fmt(fromUsd(r.modelUsd)) : "—"}
                </td>
                <td className="num">
                  {r.csvUsd > 0 ? fmt(fromUsd(r.csvUsd)) : "—"}
                </td>
                <td className="num delta">{fmtDelta(r.modelUsd, r.csvUsd)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {onClearExplorer ? (
        <button type="button" className="clear-btn" onClick={onClearExplorer}>
          {t("csv.compare.clear")}
        </button>
      ) : null}
      <style>{`
        .ex-vs {
          margin-top: 1rem;
          padding-top: 1rem;
          border-top: 1px solid var(--border);
        }
        .ex-vs h3 {
          font-size: 0.92rem;
          margin: 0 0 0.35rem;
        }
        .hint {
          margin: 0 0 0.65rem;
          font-size: 0.78rem;
          color: var(--muted);
          line-height: 1.45;
        }
        .table-wrap {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }
        table {
          width: 100%;
          min-width: 18rem;
          border-collapse: collapse;
          font-size: 0.82rem;
        }
        th,
        td {
          text-align: left;
          padding: 0.45rem 0.5rem;
          border-bottom: 1px solid var(--border);
        }
        th {
          color: var(--muted);
          font-weight: 600;
        }
        .num {
          text-align: right;
          font-family: var(--mono);
        }
        .delta {
          color: var(--accent);
        }
        .clear-btn {
          margin-top: 0.65rem;
          border: 1px solid var(--border);
          background: var(--surface2);
          color: var(--muted);
          padding: 0.35rem 0.6rem;
          border-radius: 8px;
          font: inherit;
          font-size: 0.78rem;
          font-weight: 600;
          cursor: pointer;
        }
        .clear-btn:hover {
          color: var(--text);
        }
        .clear-btn:focus-visible {
          outline: 2px solid var(--accent);
          outline-offset: 2px;
        }
      `}</style>
    </div>
  );
}
